import { useMemo, useState } from "react";

import {
  difference,
  flatten,
  identity,
  isEmpty,
  map,
  maxBy,
  minBy,
  sortBy,
} from "lodash/fp";
import { DateTime } from "luxon";

import styled from "@mui/material/styles/styled";

import ChartSeriesLabel from "@app/analytics/MeetingReport/components/ChartSeriesLabel";
import MultipleTimeSeriesChart, {
  SingleTimeSeriesData,
} from "@app/analytics/MeetingReport/components/MultipleTimeSeriesChart";
import useMeetingReport from "@app/analytics/MeetingReport/hooks/useMeetingReport";
import { TimeSeriesData } from "@app/common/types";

import { Column, Row } from "@rui/Stack";

import DeepDiveChartScrubber from "./DeepDiveChartScrubber";
import DeepDiveChartTooltip from "./DeepDiveChartTooltip";
import useDeepDiveChartTooltip, {
  ChartMetric,
  COLOR_BY_METRIC,
} from "./useDeepDiveChartTooltip";

const RANGE_STEP = 10;
const ALL_METRICS: ChartMetric[] = ["meetingScore", "engagement", "sentiment"];

const Container = styled(Column)(({ theme }) => ({
  backgroundColor: "#FFF",
  borderRadius: "8px",
  padding: theme.spacing(2),
  border: `1px solid ${theme.palette.shades.neutral[100]}`,
}));

const ChartWrapper = styled(Column)({ height: "270px" });

const getChartRange = (trends: TimeSeriesData[][]): [number, number] => {
  const allData = flatten(trends);
  if (isEmpty(allData)) return [0, 0];

  const min = Math.floor(minBy("y", allData)!.y / RANGE_STEP) * RANGE_STEP;
  const max = Math.ceil(maxBy("y", allData)!.y / RANGE_STEP) * RANGE_STEP;
  return [min, max];
};

const getChartDomain = (
  trends: TimeSeriesData[][],
  report?: {
    startTime?: DateTime;
    endTime?: DateTime;
  }
): [number, number] => {
  if (!report || !report.startTime || !report.endTime) return [0, 0];

  const allData = flatten(trends);

  const startTimeMs = report.startTime.toMillis();
  const endTimeMs = report.endTime.toMillis();

  if (isEmpty(allData)) return [startTimeMs, endTimeMs];

  const min = Math.min(minBy("x", allData)!.x, startTimeMs);
  const max = Math.max(maxBy("x", allData)!.x, endTimeMs);
  return [min, max];
};

const DeepDiveChart = () => {
  const { isLoading, meetingReport, speakerEvents } = useMeetingReport();

  const [enabledMetrics, setEnabledMetrics] =
    useState<ChartMetric[]>(ALL_METRICS);

  const series: SingleTimeSeriesData[] = useMemo(
    () => [
      {
        id: "meetingScore",
        data: meetingReport?.callScoreTrend ?? [],
        color: COLOR_BY_METRIC.meetingScore,
      },
      {
        id: "engagement",
        data: meetingReport?.engagementTrend ?? [],
        color: COLOR_BY_METRIC.engagement,
      },
      {
        id: "sentiment",
        data: meetingReport?.sentimentTrend ?? [],
        color: COLOR_BY_METRIC.sentiment,
      },
    ],
    [isLoading]
  );

  const domain: [number, number] = useMemo(
    () => getChartDomain(map("data", series), meetingReport),
    [series]
  );

  const range = useMemo(() => getChartRange(map("data", series)), [series]);

  const tooltip = useDeepDiveChartTooltip({
    domain,
    enabledMetrics,
    series,
    speakerEvents,
  });

  const labels: { label: string; value: ChartMetric; score?: number }[] =
    useMemo(
      () => [
        {
          label: "Read Score",
          value: "meetingScore",
          score: Math.round(meetingReport?.callScore ?? 0),
        },
        {
          label: "Engagement",
          value: "engagement",
          score: Math.round(meetingReport?.engagement ?? 0),
        },
        {
          label: "Sentiment",
          value: "sentiment",
          score: Math.round(meetingReport?.sentiment ?? 0),
        },
      ],
      [isLoading]
    );

  const handleToggleMetric = (value: ChartMetric) => () => {
    const newEnabledMetrics = enabledMetrics.includes(value)
      ? difference(enabledMetrics, [value])
      : [...enabledMetrics, value];
    setEnabledMetrics(sortBy(identity, newEnabledMetrics));
  };

  const enabledSeries = useMemo(
    () => series.filter(({ id }) => enabledMetrics.includes(id as ChartMetric)),
    [enabledMetrics, series]
  );

  const durationInSeconds = (domain[1] - domain[0]) / 1000;

  return (
    <Container>
      <Row spacing={1} flexWrap="wrap">
        {labels.map((label) => (
          <ChartSeriesLabel
            key={label.value}
            active={
              Boolean(label.score) && enabledMetrics.includes(label.value)
            }
            color={COLOR_BY_METRIC[label.value]}
            disabled={!label.score}
            label={label.label}
            onClick={handleToggleMetric(label.value)}
            score={label.score ? label.score : undefined}
          />
        ))}
      </Row>
      <ChartWrapper>
        <MultipleTimeSeriesChart
          domain={domain}
          range={range}
          rangeStep={RANGE_STEP}
          series={enabledSeries}
          tooltip={tooltip}
          renderTooltip={DeepDiveChartTooltip}
        />
      </ChartWrapper>
      <DeepDiveChartScrubber duration={durationInSeconds} />
    </Container>
  );
};

export default DeepDiveChart;
